import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { forgotPassword } from "../api/auth.api";
import wordmark from "../assets/images/woxa-cafe-wordmark.png";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!email.endsWith("@woxacorp.com")) {
      setErrorMsg("Email must end with @woxacorp.com");
      return;
    }
    setIsLoading(true);
    try {
      await forgotPassword({ email });
      // The server returns the same response for known/unknown emails, so we
      // always land on the same confirmation screen.
      setSent(true);
    } catch (err: any) {
      const status = err?.response?.status as number | undefined;
      if (status === 429) {
        setErrorMsg("Too many attempts. Please wait a minute and try again.");
      } else if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
        setErrorMsg("Can't reach the server. Check your internet connection.");
      } else {
        setErrorMsg("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans relative overflow-hidden">
      {/* === HERO PANEL === */}
      <div className="relative bg-hero text-hero-on px-5 pt-[44px] pb-[80px] overflow-hidden rounded-b-[40px]">
        <svg aria-hidden className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dotgrid" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dotgrid)" />
        </svg>
        <div className="relative">
          <div className="mb-[64px]">
            <img src={wordmark} alt="Woxa Cafe" className="h-[44px] w-auto" style={{ filter: "invert(1)" }} />
          </div>
          <p className="eyebrow-v2 text-hero-muted mb-3">Forgot password</p>
          <h1 className="font-display font-black text-[40px] leading-[1.05] tracking-tightest">
            No worries.<br />Let's reset it.
          </h1>
          <p className="text-base text-hero-muted mt-3 max-w-[280px]">
            Enter your work email and we'll send you a link to set a new password.
          </p>
        </div>
      </div>

      {/* === FORM CARD === */}
      <div className="px-5 -mt-[48px] relative z-10">
        <div className="bg-white rounded-3xl border border-ink-100 shadow-floaty px-6 pt-[28px] pb-[24px] max-w-md mx-auto">
          {sent ? (
            <div className="text-center py-2">
              <div className="w-[56px] h-[56px] rounded-full bg-ink-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-ink-1000" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="font-display font-black text-[22px] mb-2">Check your email</h2>
              <p className="text-sm text-ink-500 mb-6">
                If an account exists for <span className="font-semibold text-ink-1000">{email}</span>, a reset link is on its way. The link expires in 30 minutes.
              </p>
              <button onClick={() => navigate("/login")} className="btn-v2-primary btn-v2-lg w-full">
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-[6px]">
              <div className="field-v2">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder=" "
                  value={email}
                  onChange={(e) => setEmail(e.target.value.replace(/\s/g, ""))}
                  onKeyDown={(e) => e.key === " " && e.preventDefault()}
                />
                <label htmlFor="email">Email</label>
              </div>

              {errorMsg && (
                <div className="pt-3 text-sm text-ink-1000 font-medium flex items-center gap-2">
                  <svg className="w-[16px] h-[16px] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                  {errorMsg}
                </div>
              )}

              <button type="submit" disabled={isLoading || !email} className="btn-v2-primary btn-v2-lg w-full mt-[28px]">
                {isLoading ? (
                  <>
                    <svg className="w-[16px] h-[16px] animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" /><path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
                    Sending
                  </>
                ) : (
                  "Send reset link"
                )}
              </button>

              <button type="button" onClick={() => navigate("/login")} className="w-full text-center text-sm text-ink-500 hover:text-ink-1000 transition pt-4">
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="px-5 pb-[40px] max-w-md mx-auto text-center pt-[24px]">
        <p className="text-[11px] text-ink-400">
          © {new Date().getFullYear()} Woxa Corp · Brewed with care
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
